import { Fragment } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Newspaper, RefreshCw } from "lucide-react";
import { AdSlot } from "@/components/AdSlot";
import { ShareButtons } from "@/components/ShareButtons";
import { timeAgo } from "@/lib/site";

export const NewsCard = ({ item, index }) => (
  <article data-testid={`news-card-${index}`} className="rise group flex flex-col rounded-xl border border-slate-800 bg-[#181F2C] overflow-hidden hover:border-[#0052FF]/60 transition-colors duration-200"
    style={{ animationDelay: `${Math.min(index, 8) * 50}ms` }}>
    {item.image && (
      <Link to={`/news/${item.slug}`} tabIndex={-1} aria-hidden="true"><img src={item.image} alt={item.title} loading="lazy" decoding="async" width="640" height="160" className="h-40 w-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" /></Link>
    )}
    <div className="flex flex-1 flex-col gap-3 p-5">
      <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider">
        <span data-testid={`news-card-${index}-source`} className="text-sky-400 truncate">{item.source}</span>
        <span className="text-slate-600">•</span>
        <time dateTime={item.published} data-testid={`news-card-${index}-date`} className="text-slate-400 shrink-0">{timeAgo(item.published)}</time>
      </div>
      <h3 className="font-display text-base sm:text-lg font-semibold leading-snug text-slate-100">
        <Link to={`/news/${item.slug}`} data-testid={`news-card-${index}-link`} className="hover:text-sky-400 transition-colors">
          {item.title}
        </Link>
      </h3>
      {item.description && <p className="text-sm leading-relaxed text-slate-400 line-clamp-3">{item.description}</p>}
      <div className="mt-auto flex items-center justify-between gap-3">
        <Link to={`/news/${item.slug}`} data-testid={`news-card-${index}-read`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-white truncate">
          Read story <ArrowRight className="h-3 w-3 shrink-0" />
        </Link>
        <ShareButtons title={item.title} url={`${window.location.origin}/news/${item.slug}`} index={index} />
      </div>
    </div>
  </article>
);

const Skeleton = () => (
  <div className="grid gap-6 md:grid-cols-2">
    {[0, 1, 2, 3].map((i) => <div key={i} className="h-48 rounded-xl border border-slate-800 bg-[#181F2C] animate-pulse" />)}
  </div>
);

export const NewsFeed = ({ items, loading, error, adEvery = 4, adPrefix = "infeed" }) => {
  if (loading) return <Skeleton />;
  if (error || items.length === 0)
    return (
      <div data-testid="news-empty-state" className="rounded-xl border border-slate-800 bg-[#121722] p-8 text-center text-slate-400">
        <Newspaper className="mx-auto mb-3 h-6 w-6" />
        {error ? "News feed temporarily unavailable. Please check back shortly." : "Gathering the latest coverage — new articles appear automatically."}
      </div>
    );
  return (
    <div data-testid="news-feed" className="grid gap-6 md:grid-cols-2">
      {items.map((item, i) => (
        <Fragment key={item.key}>
          <NewsCard item={item} index={i} />
          {(i + 1) % adEvery === 0 && i < items.length - 1 && (
            <AdSlot id={`${adPrefix}-${(i + 1) / adEvery}`} variant="infeed" className="md:col-span-2" />
          )}
        </Fragment>
      ))}
    </div>
  );
};

export const FeedMeta = ({ total, lastUpdated, onRefresh }) => (
  <div className="flex flex-wrap items-center gap-3 font-mono text-[11px] uppercase tracking-wider text-slate-400">
    <span className="flex items-center gap-1.5"><span className="live-dot h-1.5 w-1.5 rounded-full bg-emerald-400" /> Auto-updating</span>
    <span data-testid="news-total">{total} articles</span>
    {lastUpdated && <span data-testid="news-last-updated">Checked {timeAgo(lastUpdated)}</span>}
    <button data-testid="news-refresh-button" onClick={onRefresh} className="flex items-center gap-1 text-sky-400 hover:text-sky-300">
      <RefreshCw className="h-3 w-3" /> Refresh
    </button>
  </div>
);
